import React, { useEffect, useState } from 'react';
import {
  Avatar as AntdAvatar,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Divider,
  Select,
  Typography,
  UploadFile
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { BlueArrow } from 'assets';
import Avatar from 'components/common/avatar/Avatar';
import dayjs from 'dayjs';
import { DirectlyCommander, TicketDetail } from 'types';
import progressIcon from '../../assets/svg/progressing-setting.svg';
import { requestform } from 'messages';
import { useLocation } from 'react-router-dom';
import { GetReasonOutageService } from '../../hooks/request-form';
import { TicketEventDto } from 'types/dto/ticket';
import { useGetFieldEmployeesQuery, useGetUserProfileQuery } from 'services';
import { CreateTicketFormType } from '../create-ticket/CreateTicketForm';
import { FormItem, Upload } from '../common';
import { FilesBucketNames, PERMISSION } from '../../utils';
import CheckPermissionButton from './CheckPermissionButton';
import { GetDirectCommanders } from '../../hooks/request-form/useContructionSiteManagementTicket';
import { UserOutlined } from '@ant-design/icons';
import { fieldTranslation } from '../../utils/tickets/ticketEventMapping';

interface RequestDetailProcessingProps {
  requestItem: TicketDetail;
  processingContent: string;
  isHistory: boolean;

  setProcessingContent?: (value: string) => void;
  setAssignUserInUnit?: (value: number) => void;
  setOutageReasonId?: (value: number) => void;
  setDatePowerRestore?: (value: Date) => void;
  setIsPowerRestore?: (value: boolean) => void;

  setFieldEmployee1?: (value: string | undefined) => void;
  setFieldEmployee2?: (value: string | undefined) => void;
  fieldEmployee1?: string;
  fieldEmployee2?: string;
  fieldTaskCaseIds?: number[];

  fileList?: UploadFile[];
  setFileList?: React.Dispatch<React.SetStateAction<UploadFile[]>>;
}

const EventItem: React.FC<{ event: TicketEventDto; isFirstEvent: boolean }> = ({ event, isFirstEvent }) => {
  //const { user: eventUser } = useUser(event.createdBy);

  return (
    <div className='relative flex items-center justify-between py-2'>
      {isFirstEvent && (
        <div className='absolute -left-8 top-1/2 -translate-y-1/2 rounded-full border bg-white p-2'>
          <BlueArrow />
        </div>
      )}

      <div className='mx-4 w-full flex-1'>
        {isFirstEvent && (
          <div className='flex items-center justify-between'>
            <div className='text-[14px] italic text-[#141414]'>
              {dayjs(event.createdAt).format('HH:mm - DD/MM/YYYY')} bởi {event?.createdByName} •{' '}
              {event?.createdByOrganizationUnitName}
            </div>
            <Avatar name={event.createdByName} size={32} />
          </div>
        )}

        <Typography.Text className='text-[16px]/[18px] font-semibold text-[#141414]'>
          {event.actionType?.name}
        </Typography.Text>

        <div className='mt-2 rounded-[5px] border bg-[#F7F8F9] p-4 text-[16px]/[20px] text-[#141414]'>
          {fieldTranslation(event.currentContent)}
        </div>
      </div>
    </div>
  );
};

//Main
const RequestDetailProcessing: React.FC<RequestDetailProcessingProps> = ({
  requestItem,
  processingContent,
  isHistory,

  setProcessingContent,
  setOutageReasonId,
  setDatePowerRestore,
  setIsPowerRestore,
  setFieldEmployee1,
  setFieldEmployee2,
  fieldEmployee1,
  fieldEmployee2,
  fieldTaskCaseIds,

  fileList,
  setFileList
}) => {
  const normFile = ({ fileList }: { fileList: UploadFile[] }) => fileList;
  const handleChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList && setFileList(newFileList);
  };
  const location = useLocation().pathname;

  const { optionsList, optionsCount, optionLoading } = GetReasonOutageService(16);

  const events = requestItem?.ticketEvents || [];

  const [directlyCommanderData, setDirectlyCommanderData] = useState<DirectlyCommander>();
  const { data: directlyCommander, isLoading: directlyCommanderLoading } = GetDirectCommanders(requestItem?.ticketId);

  useEffect(() => {
    if (directlyCommander && directlyCommander[0]) setDirectlyCommanderData(directlyCommander[0]);
  }, [directlyCommander]);

  const handleDatePowerRestoreChange = (value: dayjs.Dayjs) => setDatePowerRestore?.(value.toDate());
  const handlePowerRestoreChange = (checked: boolean) => setIsPowerRestore?.(checked);
  const [isShowAllEvents, setIsShowAllEvents] = useState(false);
  const toggleShowAll = () => {
    setIsShowAllEvents((prev) => !prev);
  };
  const sortedEvents = [...events].sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf());
  const latestEvent = sortedEvents[sortedEvents.length - 1];
  const remainingEvents = sortedEvents.slice(0, -1);

  const groupEventsByBatchUuid = (events: (TicketEventDto | undefined)[]) => {
    return events.reduce(
      (acc, event) => {
        if (!event || !event.batchUuid) return acc;

        const { batchUuid } = event;
        if (!acc[batchUuid]) {
          acc[batchUuid] = [];
        }
        acc[batchUuid].push(event);
        return acc;
      },
      {} as Record<string, TicketEventDto[]>
    );
  };

  const groupedEvents = groupEventsByBatchUuid(isShowAllEvents ? sortedEvents : latestEvent ? [latestEvent] : []);
  const userProfile = useGetUserProfileQuery();
  const teamCode = userProfile.data?.data.organizationUnit?.code;
  const shouldFetchFieldEmployees = location.includes('request-form') && requestItem?.status.code !== 'HOANTAT';
  const { data: fieldEmployeeData, isLoading: isLoadingEmployees } = useGetFieldEmployeesQuery(teamCode ?? '', {
    skip: !shouldFetchFieldEmployees
  });

  const fieldEmployeeOptions =
    fieldEmployeeData?.data?.map((employee) => ({
      label: employee.hoten,
      value: employee.soDienThoai
    })) ?? [];

  const defaultFieldEmployee1 = requestItem?.additionalInfo?.firstFieldEmployee?.trim();
  const defaultFieldEmployee2 = requestItem?.additionalInfo?.secondFieldEmployee?.trim();

  useEffect(() => {
    if (defaultFieldEmployee1) {
      setFieldEmployee1?.(defaultFieldEmployee1);
    }
    if (defaultFieldEmployee2) {
      setFieldEmployee2?.(defaultFieldEmployee2);
    }
  }, [requestItem?.ticketId, defaultFieldEmployee1, defaultFieldEmployee2, setFieldEmployee1, setFieldEmployee2]); // Added more specific dependencies

  useEffect(() => {
    // Initialize powerRestorationAt with current date if not already set and component is active for editing
    const shouldSetDefaultDate =
      setDatePowerRestore &&
      //!requestItem?.additionalInfo?.powerRestorationAt && // Only if no date is already set from requestItem
      requestItem?.serviceType?.code === 'baoMatDien' && // Relevant for "baoMatDien"
      requestItem?.status.code !== 'CHOTIEPNHAN' && // DatePicker section is visible
      requestItem?.status.code !== 'HOANTAT' && // DatePicker is editable
      !isHistory && // DatePicker is not disabled due to history view
      location.includes('request-form'); // Component is in form editing mode

    if (shouldSetDefaultDate) {
      setDatePowerRestore(dayjs().toDate());
    }
  }, [
    requestItem?.additionalInfo?.powerRestorationAt,
    requestItem?.serviceType?.code,
    requestItem?.status?.code,
    isHistory,
    location,
    setDatePowerRestore
  ]);

  return (
    <Card className='border-none'>
      {requestItem?.serviceType?.code === 'baoMatDien' && requestItem?.status?.code !== 'CHOTIEPNHAN' && (
        <div className='space-y-2 pb-5'>
          <div className='flex w-full items-center space-x-6'>
            <div className='w-1/5'>
              <Typography.Text className='text-nowrap text-[16px]/[16px] text-[#6C737E]'>
                {requestform.reasonOutage}
              </Typography.Text>
            </div>
            {requestItem?.status.code !== 'CHOTIEPNHAN' && requestItem?.status?.code !== 'HOANTAT' ? (
              <Select
                loading={optionLoading}
                className='w-full'
                disabled={requestItem?.status?.code === 'HOANTAT' || isHistory}
                defaultValue={
                  requestItem?.additionalInfo?.outageReasonId != null
                    ? optionsList.find((option) => option.optionId === requestItem?.additionalInfo?.outageReasonId)
                        ?.optionId
                    : optionsList[0]?.optionId
                }
                style={{ width: 200 }}
                onChange={(value) => setOutageReasonId && setOutageReasonId(value)}
                options={[
                  ...optionsList.map((option) => ({
                    value: option.optionId,
                    label: option.code + ' ' + option.name
                  }))
                ]}
              />
            ) : (
              <Typography.Text className='text-[16px]/[16px] text-[#000000]'>
                {requestItem?.additionalInfo?.outageReasonId != null
                  ? optionsList.find((option) => option.optionId === requestItem?.additionalInfo?.outageReasonId)?.name
                  : optionsList[0]?.name}
              </Typography.Text>
            )}
          </div>

          <div className='flex w-full items-center space-x-3 pb-[20px]'>
            <div className='w-1/5'>
              <Typography.Text className='text-[16px]/[16px] text-[#6C737E]'>{requestform.timeOutage}</Typography.Text>
            </div>

            <div className='flex w-full items-center justify-between'>
              <Typography.Text className='text-[16px]/[16px] text-[#000000]'>
                {requestItem?.additionalInfo?.outageAt
                  ? dayjs(requestItem?.additionalInfo?.outageAt).format('DD/MM/YYYY HH:mm')
                  : ''}
              </Typography.Text>

              <div>
                <div className='flex items-center space-x-3'>
                  <Typography.Text className='text-[16px]/[16px] text-[#6C737E]'>
                    {requestform.datePowerRestored}
                  </Typography.Text>
                  {requestItem?.status.code !== 'CHOTIEPNHAN' && requestItem?.status?.code !== 'HOANTAT' ? (
                    <DatePicker
                      value={
                        requestItem?.additionalInfo?.powerRestorationAt
                          ? dayjs(requestItem?.additionalInfo?.powerRestorationAt)
                          : undefined
                      }
                      defaultValue={dayjs()}
                      name='powerRestorationAt'
                      showTime={{ format: 'HH:mm:ss' }}
                      format='DD/MM/YYYY HH:mm:ss'
                      onChange={handleDatePowerRestoreChange}
                      placeholder='Ngày giờ có điện'
                      disabled={requestItem?.status?.code === 'HOANTAT' || isHistory}
                      allowClear={false}
                    />
                  ) : (
                    <Typography.Text className='text-[16px]/[16px] text-[#000000]'>
                      {requestItem?.additionalInfo?.powerRestorationAt
                        ? dayjs(requestItem?.additionalInfo?.powerRestorationAt).format('DD/MM/YYYY HH:mm')
                        : ''}
                    </Typography.Text>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Divider className='mt-[20px] bg-[#D4D8DE]' />
        </div>
      )}
      {/** --- PROCESSING TIMELINE --- */}
      <div className='flex items-center space-x-3'>
        <img src={progressIcon} alt='' />
        <Typography.Text className='text-[18px]/[22px] font-semibold text-[#141414]'>
          {requestform.processingTitle}
        </Typography.Text>
      </div>
      {!isHistory && (
        <>
          {requestItem?.status.code !== 'HOANTAT' &&
            requestItem?.status.code !== 'CHOTIEPNHAN' &&
            requestItem?.serviceType?.code === 'baoMatDien' &&
            location.includes('request-form') && (
              <Checkbox className='mt-3' onChange={(e) => handlePowerRestoreChange(e.target.checked)}>
                <Typography.Text className='text-[16px]/[16px] text-[#000000]'>
                  {requestform.isPowerRestore}
                </Typography.Text>
              </Checkbox>
            )}
        </>
      )}
      <section className={`mt-4 flex flex-row items-center gap-9`}>
        <Typography.Text className='text-nowrap text-[16px]/[16px] text-[#6C737E]'>Giao nhân viên: </Typography.Text>
        {requestItem?.ticketDistributions.find(
          (ticketDistribution) => ticketDistribution.isPrimary && ticketDistribution.assigneeId !== null
        ) ? (
          <div>
            <Typography.Text className='text-nowrap text-[16px]/[16px] text-[#02D8A0]'>
              {
                requestItem?.ticketDistributions.find(
                  (ticketDistribution) => ticketDistribution.isPrimary && ticketDistribution.assigneeId !== null
                )?.assigneeName
              }
            </Typography.Text>
          </div>
        ) : (
          <Typography.Text className='text-nowrap text-[16px]/[16px] text-[#FF9500]'>
            {requestform.notAssigneeEmployee}
          </Typography.Text>
        )}
      </section>
      {/**  --- Choose Employee ---*/}
      {location.includes('request-form') && (
        <section className='mt-4 flex flex-col gap-5'>
          <div>
            {requestItem.status.code !== 'CHOTIEPNHAN' && (
              <div className='flex gap-8'>
                <div className='flex w-1/2 flex-row items-center gap-2'>
                  <Typography.Text className='text-nowrap text-[16px]/[16px] text-[#6C737E]'>
                    NV hiện trường 1:
                  </Typography.Text>
                  <Select
                    className='w-full'
                    placeholder='Chọn nhân viên'
                    loading={isLoadingEmployees}
                    options={[{ label: 'Hủy giao nhân viên', value: '' }, ...fieldEmployeeOptions]}
                    onChange={(value) => setFieldEmployee1?.(value)}
                    value={fieldEmployee1}
                    showSearch
                    disabled={isHistory || requestItem.status.code == 'HOANTAT'}
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </div>
                <div className='flex w-1/2 flex-row items-center gap-2'>
                  <Typography.Text className='text-nowrap text-[16px]/[16px] text-[#6C737E]'>
                    NV hiện trường 2:
                  </Typography.Text>
                  <Select
                    className='w-full'
                    placeholder='Chọn nhân viên'
                    loading={isLoadingEmployees}
                    options={[{ label: 'Hủy giao nhân viên', value: null }, ...fieldEmployeeOptions]}
                    onChange={(value) => setFieldEmployee2?.(value)}
                    value={fieldEmployee2}
                    showSearch
                    disabled={isHistory || requestItem.status.code == 'HOANTAT'}
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </section>
      )}
      <CheckPermissionButton permission={PERMISSION.UPDATE_TICKET_ORG_UNIT}>
        {directlyCommanderData && directlyCommanderData?.members.length > 0 && (
          <section className='mt-4 flex flex-col gap-2'>
            <div className='flex w-1/2 flex-row items-center gap-2'>
              <Typography.Text className='text-nowrap text-[16px]/[16px] text-[#6C737E]'>
                Thông tin chỉ huy trực tiếp App Công trường
              </Typography.Text>
            </div>

            <div className={'ml-0 flex w-full flex-col gap-2'}>
              {directlyCommanderData?.members.map((data, ind) => (
                <div key={ind} className='flex w-full space-x-4'>
                  <div className='flex w-1/3 gap-2'>
                    <div className={'bottom-2'}>
                      <UserOutlined />
                    </div>
                    <Typography.Text className='mt-1 text-[16px]/[16px] text-[#6C737E]'>
                      {data?.role || ''}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text className='mt-1 flex-wrap text-[16px]/[20px] text-[#141414]'>
                      {data?.name || ''}
                    </Typography.Text>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </CheckPermissionButton>
      {/** --- TIMELINE --- */}
      <section className='pt-5'>
        {!isShowAllEvents && remainingEvents.length > 0 && (
          <div className='relative ml-0.5 mt-2 flex items-center'>
            <AntdAvatar.Group maxCount={5}>
              {remainingEvents.map((event, index) => (
                <AntdAvatar
                  key={index}
                  src={<Avatar name={event.createdByName} size={32} />}
                  size={32}
                  className={index !== 0 ? '-ml-5' : ''}
                />
              ))}
            </AntdAvatar.Group>
            <Button type='link' className='ml-2 p-0 text-[#1564E8] shadow-none' onClick={toggleShowAll}>
              Xem tất cả lịch sử xử lý
            </Button>
          </div>
        )}

        {Object.entries(groupedEvents).map(([batchUuid, batchEvents]) => (
          <div key={batchUuid} className='relative mx-4 border-l pb-6 pl-4'>
            {(batchEvents as TicketEventDto[]).map((event, eventIdx) => (
              <EventItem key={event.ticketEventId} event={event} isFirstEvent={eventIdx === 0} />
            ))}
          </div>
        ))}

        {isShowAllEvents && (
          <div className='relative mb-4 mt-2 flex items-center'>
            <Button type='link' className='ml-4 p-0 text-[#1564E8] shadow-none' onClick={toggleShowAll}>
              Thu gọn lịch sử xử lý
            </Button>
          </div>
        )}

        {(requestItem?.status.code !== 'CHOTIEPNHAN' &&
          requestItem?.status.code !== 'HOANTAT' &&
          location.includes('request-form')) ||
        location.includes('transfer-') ? (
          <div className={'mx-2'}>
            {!isHistory && (
              <TextArea
                placeholder='Nhập nội dung xử lý'
                autoSize={{ minRows: 5, maxRows: 10 }}
                className='no-resize! rounded-lg bg-[#F7F8F6]'
                value={processingContent}
                style={{ resize: 'none' }}
                onChange={(e) => setProcessingContent && setProcessingContent(e.target.value)}
              />
            )}
          </div>
        ) : (
          ''
        )}
      </section>
      {requestItem?.status.code !== 'CHOTIEPNHAN' &&
        requestItem?.status.code !== 'HOANTAT' &&
        (location.includes('request-form') || location.includes('transfer-')) && (
          <section className={'upload-file mt-4 w-full px-2'}>
            <FormItem<CreateTicketFormType> valuePropName='fileList' name='uploadFileIds' getValueFromEvent={normFile}>
              <Upload.ServerDragger
                className='w-full'
                multiple
                listType='picture-card'
                maxCount={5}
                fileList={fileList}
                onChange={handleChange}
                bucketName={FilesBucketNames.TICKET}
              />
            </FormItem>
          </section>
        )}
    </Card>
  );
};

export default RequestDetailProcessing;
